import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Paper,
  Modal,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import { Add, Delete, Close } from '@mui/icons-material';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../../../firebase/firebaseConfig';
import { useNotifications } from '@toolpad/core';

interface ImageOption {
  label: string;
  imageUrl: string;
}

interface ImageChoiceEditorProps {
  options: ImageOption[];
  onChange: (options: ImageOption[]) => void;
  templateId: string;
  fieldIndex: number;
}

const ImageChoiceEditor: React.FC<ImageChoiceEditorProps> = ({
  options,
  onChange,
  templateId,
  fieldIndex,
}) => {
  const [uploading, setUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const notifications = useNotifications();

  const handleFileUpload = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;

      const file = files[0];
      if (!file.type.startsWith('image/')) {
        notifications.show('Please upload an image file', { severity: 'error', autoHideDuration: 3000 });
        return;
      }

      // Limit file size to 2MB
      if (file.size > 2 * 1024 * 1024) {
        notifications.show('Image must be smaller than 2MB', { severity: 'error', autoHideDuration: 3000 });
        return;
      }

      try {
        setUploading(true);
        const timestamp = Date.now();
        const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const storagePath = `form-templates/${templateId}/field-${fieldIndex}/${timestamp}-${sanitizedName}`;
        const storageRef = ref(storage, storagePath);

        await uploadBytes(storageRef, file);
        const downloadUrl = await getDownloadURL(storageRef);

        const newOption: ImageOption = {
          label: file.name.replace(/\.[^/.]+$/, ''),
          imageUrl: downloadUrl,
        };

        onChange([...options, newOption]);
        notifications.show('Image uploaded successfully', { severity: 'success', autoHideDuration: 3000 });
      } catch (error) {
        console.error('Error uploading image:', error);
        notifications.show('Failed to upload image', { severity: 'error', autoHideDuration: 3000 });
      } finally {
        setUploading(false);
      }
    },
    [options, onChange, templateId, fieldIndex, notifications]
  );

  const handleDeleteOption = async (index: number) => {
    const option = options[index];
    try {
      // Try to delete from storage if it's a Firebase URL
      if (option.imageUrl.includes('firebasestorage.googleapis.com') || option.imageUrl.includes('firebasestorage.app')) {
        const storageRef = ref(storage, option.imageUrl);
        await deleteObject(storageRef).catch(() => {
          // Ignore errors if file doesn't exist
        });
      }
    } catch (error) {
      // Continue with removal even if storage delete fails
    }
    const newOptions = options.filter((_, i) => i !== index);
    onChange(newOptions);
  };

  const handleLabelChange = (index: number, label: string) => {
    const newOptions = options.map((opt, i) =>
      i === index ? { ...opt, label } : opt
    );
    onChange(newOptions);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFileUpload(e.dataTransfer.files);
  };

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Image Choices ({options.length})
      </Typography>

      {/* Image Grid */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        {options.map((option, idx) => (
          <Paper
            key={idx}
            variant="outlined"
            sx={{
              width: 100,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Thumbnail */}
            <Box
              component="img"
              src={option.imageUrl}
              alt={option.label}
              onClick={() => setPreviewImage(option.imageUrl)}
              sx={{
                width: '100%',
                height: 80,
                objectFit: 'cover',
                cursor: 'pointer',
                display: 'block',
              }}
            />
            {/* Delete button */}
            <IconButton
              size="small"
              onClick={() => handleDeleteOption(idx)}
              sx={{
                position: 'absolute',
                top: 2,
                right: 2,
                bgcolor: 'rgba(0,0,0,0.5)',
                color: 'white',
                p: 0.25,
                '&:hover': { bgcolor: 'error.main' },
              }}
            >
              <Delete fontSize="small" />
            </IconButton>
            {/* Label input */}
            <TextField
              value={option.label}
              onChange={(e) => handleLabelChange(idx, e.target.value)}
              size="small"
              variant="standard"
              placeholder="Label"
              inputProps={{ style: { fontSize: '0.75rem', textAlign: 'center' } }}
              sx={{ px: 0.5, pb: 0.5 }}
              fullWidth
            />
          </Paper>
        ))}

        {/* Add New Image Drop Zone */}
        <Paper
          variant="outlined"
          component="label"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            width: 100,
            height: 110,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: uploading ? 'default' : 'pointer',
            borderStyle: 'dashed',
            borderColor: dragOver ? 'primary.main' : 'divider',
            bgcolor: dragOver ? 'action.hover' : 'transparent',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'action.hover',
            },
          }}
        >
          {uploading ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <Add color="action" />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, textAlign: 'center', px: 0.5 }}>
                Drop or click
              </Typography>
            </>
          )}
          <input
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => handleFileUpload(e.target.files)}
            disabled={uploading}
          />
        </Paper>
      </Box>

      {/* Full-size Preview Modal */}
      <Modal
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 300 } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            maxWidth: '90vw',
            maxHeight: '90vh',
            outline: 'none',
          }}
        >
          <IconButton
            onClick={() => setPreviewImage(null)}
            sx={{
              position: 'absolute',
              top: -40,
              right: 0,
              color: 'white',
            }}
          >
            <Close />
          </IconButton>
          {previewImage && (
            <Box
              component="img"
              src={previewImage}
              alt="Preview"
              sx={{
                maxWidth: '90vw',
                maxHeight: '85vh',
                objectFit: 'contain',
                borderRadius: 1,
              }}
            />
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default ImageChoiceEditor;
